import { Box, CardMedia, CircularProgress, Typography } from '@mui/material';
import { memo } from 'react';
import { defineMessage, useIntl } from 'react-intl';

const posterProcessingMessage = defineMessage({
  id: 'fragments.card.posterProcessing',
  defaultMessage: 'Procesando',
});

const mediaSx = {
  width: '100%',
  height: '100%',
  bgcolor: 'grey.900',
  position: 'relative',
  overflow: 'hidden',
} as const;

type FragmentCardPosterProps = {
  posterSrc: string | null;
};

/**
 * Video card poster area: optional image or fallback background.
 * When posterSrc is null, shows a spinner and "Procesando" message.
 * Overlay pills are rendered by FragmentCardMediaOverlay in the parent.
 * Memoized so it does not re-render when fragmentId and variant are unchanged (posterSrc changes are ignored).
 */
function FragmentCardPosterInner({ posterSrc }: FragmentCardPosterProps) {
  const { $t } = useIntl();

  return (
    <CardMedia component="div" sx={mediaSx}>
      {posterSrc ? (
        <Box
          component="img"
          src={posterSrc}
          alt=""
          loading="lazy"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            bgcolor: 'grey.400',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            color: 'text.disabled',
          }}
        >
          <CircularProgress size={12} color="inherit" />
          <Typography variant="caption">{$t(posterProcessingMessage)}</Typography>
        </Box>
      )}
    </CardMedia>
  );
}

export const FragmentCardPoster = memo(FragmentCardPosterInner);
